/**
 * The roads, rails and waterways, dated.
 *
 * Every route carries the year it opened and, where it applies, the year it
 * went. Draw the map for 1948 and the Thruway is not on it; draw it for 1962
 * and the Thruway is there but the Northway stops at Lake George. That is the
 * whole trick of the year slider, and it is honest because the dates are.
 *
 * DATES, AND WHERE THEY COME FROM. The Erie Canal opened end to end in
 * October 1825. The New York Central's Albany to Buffalo line was a chain of
 * short railroads consolidated in 1853, running through since the early
 * 1840s. Dr Webb's Adirondack line from Utica to Lake Placid opened in 1892.
 * The Delaware and Hudson's Adirondack branch reached North Creek in 1871.
 * The Thruway's first stretch opened in June 1954 and the Albany to Buffalo
 * mainline was through by the end of that year; New York City was joined in
 * 1955 and 1956. The Northway was built in stages from 1957 and its last
 * section, through the High Peaks, opened in 1967. The Quickway (Route 17)
 * grew through the 1950s and 1960s; 1960 is when most of it was driveable.
 */

import type { RouteDef } from './types';

export const ROUTES: readonly RouteDef[] = [
  {
    id: 'hudson-river',
    name: 'The Hudson by sloop',
    mode: 'water',
    from: 1600,
    via: ['nyc', 'yonkers', 'tarrytown', 'peekskill', 'westpoint', 'newburgh', 'poughkeepsie', 'kingston', 'catskill', 'hudson', 'albany', 'troy'],
  },
  {
    id: 'erie-canal',
    name: 'The Erie Canal',
    mode: 'water',
    from: 1825,
    via: ['albany', 'cohoes', 'schenectady', 'amsterdam', 'fonda', 'canajoharie', 'fortplain', 'littlefalls', 'herkimer', 'utica', 'rome', 'oneida', 'chittenango', 'syracuse', 'weedsport', 'montezuma', 'lyons', 'palmyra', 'rochester', 'brockport', 'medina', 'lockport', 'tonawanda', 'buffalo'],
  },
  {
    id: 'champlain-canal',
    name: 'The Champlain Canal',
    mode: 'water',
    from: 1823,
    via: ['troy', 'schaghticoke', 'whitehall'],
  },
  {
    id: 'nyc-water-level',
    name: 'New York Central, the Water Level Route',
    mode: 'rail',
    from: 1842,
    via: ['nyc', 'yonkers', 'tarrytown', 'peekskill', 'poughkeepsie', 'rhinebeck', 'hudson', 'albany', 'schenectady', 'amsterdam', 'fonda', 'canajoharie', 'littlefalls', 'herkimer', 'utica', 'rome', 'oneida', 'syracuse', 'weedsport', 'lyons', 'palmyra', 'rochester', 'batavia', 'buffalo'],
  },
  {
    id: 'dh-north',
    name: 'Delaware and Hudson to Montreal',
    mode: 'rail',
    from: 1875,
    via: ['albany', 'cohoes', 'ballstonspa', 'saratoga', 'whitehall', 'ticonderoga', 'crownpoint', 'porthenry', 'westport', 'peru', 'plattsburgh', 'rousespoint'],
  },
  {
    id: 'dh-northcreek',
    name: 'The Adirondack branch to North Creek',
    mode: 'rail',
    from: 1871,
    via: ['saratoga', 'corinth', 'lakeluzerne', 'warrensburg', 'northcreek'],
  },
  {
    id: 'webb-adirondack',
    name: "Dr Webb's Adirondack line",
    mode: 'rail',
    from: 1892,
    to: 1972,
    via: ['utica', 'remsen', 'thendara', 'bigmoose', 'tupperlake', 'saranaclake', 'lakeplacid'],
  },
  {
    id: 'erie-southern-tier',
    name: 'The Erie Railroad',
    mode: 'rail',
    from: 1851,
    via: ['suffern', 'harriman', 'monticello', 'hancock', 'deposit', 'binghamton', 'owego', 'elmira', 'corning', 'hornell', 'olean', 'salamanca', 'jamestown'],
  },
  {
    id: 'fjg-sacandaga',
    name: 'The FJ and G to Northville',
    mode: 'rail',
    from: 1875,
    to: 1930,
    via: ['gloversville', 'mayfield', 'cranberrycreek', 'sacandagapark', 'northville'],
  },
  {
    id: 'route-9',
    name: 'Route 9',
    mode: 'road',
    from: 1900,
    via: ['nyc', 'yonkers', 'tarrytown', 'peekskill', 'poughkeepsie', 'rhinebeck', 'hudson', 'valatie', 'albany', 'cliftonpark', 'saratoga', 'glensfalls', 'lakegeorge', 'warrensburg', 'chestertown', 'pottersville', 'schroonlake', 'northhudson', 'elizabethtown', 'keeseville', 'peru', 'plattsburgh', 'champlain'],
  },
  {
    id: 'route-9n',
    name: 'Route 9N',
    mode: 'road',
    from: 1900,
    via: ['saratoga', 'corinth', 'lakeluzerne', 'lakegeorge', 'boltonlanding', 'hague', 'ticonderoga', 'westport', 'elizabethtown', 'keene', 'lakeplacid', 'saranaclake'],
  },
  {
    id: 'route-9w',
    name: 'Route 9W',
    mode: 'road',
    from: 1900,
    via: ['nyc', 'nyack', 'bearmountain', 'westpoint', 'newburgh', 'kingston', 'saugerties', 'catskill', 'coxsackie', 'albany'],
  },
  {
    id: 'route-20',
    name: 'Route 20',
    mode: 'road',
    from: 1900,
    via: ['albany', 'duanesburg', 'sharonsprings', 'cherryvalley', 'richfieldsprings', 'cazenovia', 'pompey', 'skaneateles', 'auburn', 'senecafalls', 'waterloo', 'geneva', 'canandaigua', 'avon', 'leroy', 'buffalo'],
  },
  {
    id: 'route-5',
    name: 'Route 5',
    mode: 'road',
    from: 1900,
    via: ['albany', 'schenectady', 'amsterdam', 'fonda', 'canajoharie', 'littlefalls', 'herkimer', 'utica', 'oneida', 'chittenango', 'syracuse', 'auburn', 'senecafalls', 'geneva', 'canandaigua', 'avon', 'batavia', 'buffalo'],
  },
  {
    id: 'route-28',
    name: 'Route 28',
    mode: 'road',
    from: 1900,
    via: ['kingston', 'phoenicia', 'margaretville', 'oneonta', 'cooperstown', 'richfieldsprings', 'herkimer', 'utica', 'remsen', 'thendara', 'oldforge', 'inlet', 'raquettelake', 'bluemountainlake', 'indianlake', 'northcreek', 'warrensburg'],
  },
  {
    id: 'route-30',
    name: 'Route 30',
    mode: 'road',
    from: 1900,
    via: ['gilboa', 'amsterdam', 'broadalbin', 'mayfield', 'northville', 'wells', 'speculator', 'indianlake', 'longlake', 'tupperlake', 'malone'],
  },
  {
    id: 'route-3',
    name: 'Route 3',
    mode: 'road',
    from: 1900,
    via: ['watertown', 'starlake', 'cranberrylake', 'tupperlake', 'saranaclake', 'plattsburgh'],
  },
  {
    id: 'route-17-old',
    name: 'Route 17',
    mode: 'road',
    from: 1900,
    via: ['harriman', 'monticello', 'liberty', 'roscoe', 'hancock', 'deposit', 'binghamton', 'owego', 'elmira', 'corning', 'hornell', 'olean', 'salamanca', 'jamestown'],
  },
  {
    id: 'thruway',
    name: 'The Thruway',
    mode: 'expressway',
    from: 1954,
    via: ['yonkers', 'nyack', 'suffern', 'harriman', 'newburgh', 'kingston', 'catskill', 'albany', 'schenectady', 'amsterdam', 'canajoharie', 'littlefalls', 'utica', 'syracuse', 'weedsport', 'rochester', 'leroy', 'batavia', 'cheektowaga', 'buffalo'],
  },
  {
    id: 'northway',
    name: 'The Northway',
    mode: 'expressway',
    from: 1967,
    via: ['albany', 'cliftonpark', 'saratoga', 'glensfalls', 'lakegeorge', 'warrensburg', 'pottersville', 'schroonlake', 'northhudson', 'keenevalley', 'elizabethtown', 'keeseville', 'peru', 'plattsburgh', 'champlain'],
  },
  {
    id: 'northway-south',
    name: 'The Northway, first stretch',
    mode: 'expressway',
    from: 1961,
    to: 1967,
    via: ['albany', 'cliftonpark', 'saratoga', 'glensfalls', 'lakegeorge'],
  },
  {
    id: 'quickway',
    name: 'The Quickway',
    mode: 'expressway',
    from: 1960,
    via: ['harriman', 'monticello', 'liberty', 'roscoe', 'hancock', 'deposit', 'binghamton', 'owego', 'elmira', 'corning', 'hornell', 'olean', 'salamanca', 'jamestown'],
  },
  {
    id: 'i81',
    name: 'Interstate 81',
    mode: 'expressway',
    from: 1968,
    via: ['binghamton', 'cortland', 'homer', 'syracuse', 'watertown'],
  },
];
